import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { api, fmtCredits, navigate } from '../lib.jsx';
import { Icon, PlatformDot } from '../ui.jsx';

const SCENES = ['hello', 'what', 'choose'];
const FADE = 650; // ms; matches .ob-leaving
const HOLD = 3400; // ms the welcome stays before it fades

const COMMENTS = [
  { text: 'Honestly the best one I’ve tried.', tag: 'Praise', ink: 'var(--good-ink)', soft: 'var(--good-soft)' },
  { text: 'Wish it didn’t sell out so fast.', tag: 'Complaint', ink: 'var(--bad-ink)', soft: 'var(--bad-soft)' },
  { text: 'Is there a bigger size?', tag: 'Question', ink: 'var(--brand-ink)', soft: 'var(--accent-soft)' },
];
const THEMES = [['Quality', 64], ['Stock', 41], ['Sizes', 22]];
const d = (s) => ({ '--d': `${s}s` });

/** Collect → read → report, played once in CSS. With reduced motion everything is simply there. */
function WhatScene({ onNext }) {
  return (
    <>
      <p className="eyebrow ob-in">What Yapscape does</p>
      <h1 className="ob-in" style={d(0.1)}>We read what people <em>already post</em> about you.</h1>
      <p className="ob-lede muted ob-in" style={d(0.35)}>Public posts and comments on TikTok, Instagram, Reddit, YouTube and LinkedIn, and Amazon reviews, sorted into what people praise, criticise and ask about.</p>
      <div className="ob-flow">
        <div className="ob-card ob-in" style={d(0.8)}>
          <p className="ob-step"><b>01</b>Collect</p>
          <div className="ob-chips">{['tiktok', 'instagram', 'reddit', 'youtube', 'linkedin', 'amazon'].map((p, i) => <span key={p} className="ob-pop" style={d(1 + i * 0.12)}><PlatformDot p={p} /></span>)}</div>
          <p className="small muted">Posts and comments from the market and dates you choose.</p>
        </div>
        <i className="ob-link ob-in" style={d(1.9)} aria-hidden="true" />
        <div className="ob-card ob-in" style={d(2.1)}>
          <p className="ob-step"><b>02</b>Read</p>
          <ul className="ob-comments">
            {COMMENTS.map((c, i) => (
              <li key={c.tag} className="ob-in" style={d(2.35 + i * 0.45)}>
                <span>“{c.text}”</span>
                <span className="ob-tag ob-pop" style={{ ...d(2.7 + i * 0.45), '--tc': c.ink, '--tb': c.soft }}>{c.tag}</span>
              </li>
            ))}
          </ul>
        </div>
        <i className="ob-link ob-in" style={d(3.9)} aria-hidden="true" />
        <div className="ob-card ob-in" style={d(4.1)}>
          <p className="ob-step"><b>03</b>Report</p>
          <p className="ob-headline">People love the quality and want it back in stock.</p>
          <div className="ob-sent ob-wipe" style={d(4.4)} role="img" aria-label="58% positive, 17% mixed, 25% negative">
            <i style={{ flex: 58, background: 'var(--pos)' }} /><i style={{ flex: 17, background: 'var(--mix)' }} /><i style={{ flex: 25, background: 'var(--neg)' }} />
          </div>
          <ul className="ob-themes">
            {THEMES.map(([t, n], i) => <li key={t}><span>{t}</span><span className="track"><i className="ob-wipe" style={{ ...d(4.7 + i * 0.2), width: `${n}%` }} /></span><span className="small muted">{n}%</span></li>)}
          </ul>
          <p className="small muted">Every finding links back to the comments behind it.</p>
        </div>
      </div>
      <button type="button" className="btn primary ob-cta ob-in" style={d(5.2)} onClick={onNext}>Let’s dive in <Icon name="arrow" /></button>
    </>
  );
}

/** First sign-in, once: welcome → what Yapscape does → two ways to start. */
export function Welcome({ user, credits, onDone }) {
  const [scene, setScene] = useState('hello');
  const [leaving, setLeaving] = useState(false);
  const stage = useRef(null), main = useRef(null);
  const [fit, setFit] = useState(1);
  const go = (next) => { setLeaving(true); setTimeout(() => { setScene(next); setLeaving(false); }, FADE); };
  // Seen as soon as it starts: it never plays again, even if they reload or leave halfway.
  useEffect(() => { api('/api/me/onboarded', { body: {} }).catch(() => {}); }, []);
  useEffect(() => { stage.current?.focus({ preventScroll: true }); }, [scene]);
  // The screen never scrolls: a scene taller than the window is scaled down until it fits.
  useLayoutEffect(() => {
    const measure = () => {
      const m = main.current, s = stage.current;
      if (!m || !s) return;
      const cs = getComputedStyle(m);
      const room = m.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
      setFit(Math.min(1, room / s.offsetHeight));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(main.current);
    ro.observe(stage.current);
    return () => ro.disconnect();
  }, [scene]);
  useEffect(() => {
    if (scene !== 'hello' || leaving) return;
    const t = setTimeout(() => go('what'), HOLD);
    return () => clearTimeout(t);
  }, [scene, leaving]);

  function start(to) {
    onDone();
    navigate(to);
  }
  const first = (user.name ?? '').split(' ')[0];

  return (
    <main ref={main} className={`ob${leaving ? ' ob-leaving' : ''}`} aria-label="Welcome to Yapscape">
      {scene !== 'choose' ? <button type="button" className="btn ghost small ob-skip" onClick={() => go('choose')}>Skip intro</button> : null}
      <div className="ob-fit" style={{ '--fit': fit }}>
        <div className={`ob-stage ob-${scene}`} key={scene} ref={stage} tabIndex={-1}>
          {scene === 'hello' ? <>
            <img className="ob-mark ob-in" src="/assets/mark.png" alt="" width="64" height="64" />
            {/* Their first name when the admin gave one, else the company (always given). */}
            <h1 className="ob-in" style={d(0.25)}>Welcome, <em className="ob-name">{first || user.workspace.name}</em>.</h1>
            <p className="ob-in muted" style={d(1)}>{first ? `Yapscape is ready for ${user.workspace.name}.` : 'Thank you for choosing Yapscape.'}</p>
            <span className="ob-timer" aria-hidden="true" />
          </> : null}
          {scene === 'what' ? <WhatScene onNext={() => go('choose')} /> : null}
          {scene === 'choose' ? <>
            <p className="eyebrow ob-in">Let’s dive in</p>
            <h1 className="ob-in" style={d(0.1)}>Where would you like to <em>start</em>?</h1>
            <div className="ob-options">
              <button type="button" className="ob-option primary ob-in" style={d(0.3)} onClick={() => start('/new')}>
                <span className="ob-icon"><Icon name="plus" size={22} /></span>
                <span className="ob-option-title">Create your first report</span>
                <span className="ob-option-text">Name a brand, a product, or two to compare. We collect the posts and comments and write the report.</span>
                {credits && !credits.team ? <span className="ob-option-note">You have {fmtCredits(credits.left)} credits to start with</span> : null}
                <span className="ob-option-go">Start research</span>
              </button>
              <button type="button" className="ob-option ob-in" style={d(0.42)} onClick={() => start('/sample')}>
                <span className="ob-icon"><Icon name="reports" size={22} /></span>
                <span className="ob-option-title">View a sample report</span>
                <span className="ob-option-text">A finished report on real public comments: see what you get before you start. It uses no credits.</span>
                <span className="ob-option-go">Open the sample</span>
              </button>
            </div>
          </> : null}
        </div>
      </div>
      <div className="ob-dots" aria-hidden="true">{SCENES.map((s) => <i key={s} className={s === scene ? 'on' : ''} />)}</div>
    </main>
  );
}
